'use client';

import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

export function useCarousel({ loop = false }: { loop?: boolean } = {}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    align: 'start',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [snapCount, setSnapCount] = useState(0);

  const sync = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
    setSnapCount(api.scrollSnapList().length);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => sync(emblaApi);
    const onReInit = () => sync(emblaApi);

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);

    const rafId = requestAnimationFrame(() => sync(emblaApi));

    return () => {
      cancelAnimationFrame(rafId);
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi, sync]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  return {
    emblaRef,
    selectedIndex,
    canPrev,
    canNext,
    snapCount,
    scrollPrev,
    scrollNext,
    scrollTo,
  };
}
